import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";
import { AuthState } from "./auth";

export interface AnalyticsState {
  request_time_series: any[];
  request_category: any[];
  request_rooms: any[];
  response_time: {
    average: any;
    num_samples: number;
  };
}

export interface LogState {
  actionable_requests: any[];
  non_actionable_requests: any[];
  content: any[];
}

// Define a type for the slice state
export interface InsightsState {
  analytics: {
    data: AnalyticsState | null;
    date_range: {
      start: string | null;
      end: string | null;
    };
    loading: boolean;
    waiting: boolean;
    errors: string[];
    export: any[] | null;
  };
  log: {
    data: LogState | null;
    date_range: {
      start: string | null;
      end: string | null;
    };
    loading: boolean;
    waiting: boolean;
    errors: string[];
    export: {
      actionable_requests: any[];
      non_actionable_requests: any[];
      content: any[];
    } | null;
  };
  options: {
    [key: string]: any;
  };
}

// Define the initial state using that type
const initialState: InsightsState = {
  analytics: {
    data: null,
    date_range: {
      start: null,
      end: null,
    },
    loading: false,
    waiting: false,
    errors: [],
    export: null,
  },
  log: {
    data: null,
    date_range: {
      start: null,
      end: null,
    },
    loading: false,
    waiting: false,
    errors: [],
    export: null,
  },
  options: {},
};

//////////////////////////////
//////// Async Thunks ////////
//////////////////////////////

// Get analytics for property
export const getAnalytics = createAsyncThunk(
  "insights/analytics/get",
  async (
    obj: {
      dateRange: string | null;
    },
    { getState, rejectWithValue },
  ) => {
    try {
      // Get user data from store
      const { auth } = getState() as { auth: AuthState };

      // Configure authorization header with user's token
      const config = { headers: { Authorization: `Bearer ${auth.access}` } };

      // Build endpoint URL
      let url = `/api/analytics/`;
      if (obj.dateRange) url += `?dateRange=${obj.dateRange}`;

      // Send request
      const res = await axios.get(url, config);
      return { data: res.data, status: res.status };
    } catch (err: any) {
      if (err.response && err.response.data.message) {
        return rejectWithValue(err.response.data.message);
      } else {
        return rejectWithValue(err.message);
      }
    }
  },
);

// Get log for property
export const getLog = createAsyncThunk(
  "insights/log/get",
  async (
    obj: {
      dateRange: string | null;
    },
    { getState, rejectWithValue },
  ) => {
    try {
      // Get user data from store
      const { auth } = getState() as { auth: AuthState };

      // Configure authorization header with user's token
      const config = { headers: { Authorization: `Bearer ${auth.access}` } };

      // Build endpoint URL
      let url = `/api/log/`;
      if (obj.dateRange) url += `?dateRange=${obj.dateRange}`;

      // Send request
      const res = await axios.get(url, config);
      return { data: res.data, status: res.status };
    } catch (err: any) {
      if (err.response && err.response.data.message) {
        return rejectWithValue(err.response.data.message);
      } else {
        return rejectWithValue(err.message);
      }
    }
  },
);

// Undo actionable request logs
export const undoActionableRequestLog = createAsyncThunk(
  "insights/log/actionable/undo/put",
  async (
    obj: {
      logIds: string[];
    },
    { getState, rejectWithValue },
  ) => {
    try {
      // Get user data from store
      const { auth } = getState() as { auth: AuthState };

      // Configure authorization header with user's token
      const config = { headers: { Authorization: `Bearer ${auth.access}` } };

      // Build body
      const body = { id: obj.logIds };
      const res = await axios.put("/api/log/undo/", body, config);
      return { data: res.data, status: res.status };
    } catch (err: any) {
      if (err.response && err.response.data.message) {
        return rejectWithValue(err.response.data.message);
      } else {
        return rejectWithValue(err.message);
      }
    }
  },
);

/////////////////////////////
//////// Redux slice ////////
/////////////////////////////

const insightsSlice = createSlice({
  name: "insights",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder

      // Get analytics for property requests
      .addCase(getAnalytics.pending, (state) => {
        state.analytics.loading = true;
        state.analytics.waiting = false;
      })
      .addCase(getAnalytics.fulfilled, (state, action) => {
        state.analytics.data = action.payload.data.data;
        state.analytics.export = action.payload.data.export;
        state.options = action.payload.data.options;
        state.analytics.date_range.start = action.payload.data.start_date;
        state.analytics.date_range.end = action.payload.data.end_date;
        state.analytics.loading = false;
        state.analytics.waiting = false;
      })
      .addCase(getAnalytics.rejected, (state) => {
        state.analytics.loading = false;
        state.analytics.waiting = false;
      })

      // Get logs for property requests
      .addCase(getLog.pending, (state) => {
        state.log.loading = true;
        state.log.waiting = false;
      })
      .addCase(getLog.fulfilled, (state, action) => {
        state.log.data = action.payload.data.data;
        state.options = action.payload.data.options;
        state.log.export = action.payload.data.export;
        state.log.date_range.start = action.payload.data.start_date;
        state.log.date_range.end = action.payload.data.end_date;
        state.log.loading = false;
        state.log.waiting = false;
      })
      .addCase(getLog.rejected, (state) => {
        state.log.loading = false;
        state.log.waiting = false;
      })

      // Undo actionable request logs
      .addCase(undoActionableRequestLog.pending, (state) => {
        state.log.waiting = true;
      })
      .addCase(undoActionableRequestLog.fulfilled, (state, action) => {
        const logsToRemove = action.payload.data.id;
        const previousData =
          state.log === null
            ? []
            : state.log.data === null
            ? []
            : state.log.data.actionable_requests;
        const newData = previousData.filter(
          (log) => !logsToRemove.includes(log.id),
        );
        if (state.log !== null && state.log.data !== null) {
          state.log.data.actionable_requests = newData;
        }
        state.log.waiting = false;
      })
      .addCase(undoActionableRequestLog.rejected, (state) => {
        state.log.waiting = false;
      });
  },
});

export default insightsSlice.reducer;
